import React, { useContext, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";

import { generateFilename } from "../utils/index.js";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor.js";

const DropArea = styled('div')(({ theme }) => ({
  border: '2px dashed #aaa',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
}));

export default function UploadCin7Dialog({ onUploadSuccess, confirmationUrl }) {
  const [cin7File, setCin7File] = useState(null);
  const [loading, setLoading] = useState(false);

  const context = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const handleChangeFile = (files) => {
    setCin7File(files[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => handleChangeFile(acceptedFiles),
    maxFiles: 1,
    maxSize: 5000000,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    }
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('file', cin7File);

      const response = await fetchWithInterceptor('/api/integrations/convert-cin7-to-aglc', {
        method: 'POST',
        body: formData,
        headers: {
          'x-access-token': authContext.getToken(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to process file');
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      if (confirmationUrl) {
        a.download = `${generateFilename("aglc_products")}.csv`;
      } else {
        a.download = `${generateFilename("products_to_order")}.csv`;
      }
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);

      if (onUploadSuccess) {
        onUploadSuccess({
          uploadedFileUrl: url,
        });
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
      context.showMessage("Error processing file", "error");
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ minHeight: 200 }}>
            <DropArea {...getRootProps()}>
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography sx={{ textAlign: 'center', fontSize: '1.5rem' }}>
                Drag and drop a Cin7 CSV file here or click
              </Typography>
            </DropArea>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>
              {`Uploaded file: ${cin7File ? cin7File.name : '-'}`}
            </Typography>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!cin7File || loading}
              sx={{ width: 100, height: 36.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
